import {
    type Ability,
    type AnyPermission,
    type BasicPermissions,
    basicDenyPermission,
    type MedicalPermissionSection,
    MedicalSections,
    type NullablePermission,
    nullPermission,
    type PartialPermissionEntry,
    type PartialPermissionSection,
    type PartialProfilePermissions,
    type Permission,
    type PermissionEntry,
    type PermissionSectionEntry,
    type ProfilePermissions,
    type ResolvedAbility,
    type Section,
    type TeamPermissions,
    teamDenyPermission,
} from "@qendo/permissions";

export class Permissions implements ProfilePermissions {
    public readonly profile: BasicPermissions;
    public readonly emergency: BasicPermissions;
    public readonly goals: BasicPermissions;
    public readonly journal: BasicPermissions;
    public readonly activities: BasicPermissions;
    public readonly symptoms: BasicPermissions;
    public readonly cycle: BasicPermissions;
    public readonly calendar: BasicPermissions;
    public readonly documents: BasicPermissions;
    public readonly medical: BasicPermissions;
    public readonly allergies: NullablePermission;
    public readonly conditions: NullablePermission;
    public readonly immunisations: NullablePermission;
    public readonly medications: NullablePermission;
    public readonly past_symptoms: NullablePermission;
    public readonly supplements: NullablePermission;
    public readonly surgeries: NullablePermission;
    public readonly triggers: NullablePermission;
    public readonly team: TeamPermissions;
    public readonly photos: BasicPermissions;

    public constructor(permissions?: PartialProfilePermissions | null) {
        this.profile = Permissions.fillPermission(basicDenyPermission, permissions?.profile);
        this.emergency = Permissions.fillPermission(basicDenyPermission, permissions?.emergency);
        this.goals = Permissions.fillPermission(basicDenyPermission, permissions?.goals);
        this.journal = Permissions.fillPermission(basicDenyPermission, permissions?.journal);
        this.activities = Permissions.fillPermission(basicDenyPermission, permissions?.activities);
        this.symptoms = Permissions.fillPermission(basicDenyPermission, permissions?.symptoms);
        this.cycle = Permissions.fillPermission(basicDenyPermission, permissions?.cycle);
        this.calendar = Permissions.fillPermission(basicDenyPermission, permissions?.calendar);
        this.documents = Permissions.fillPermission(basicDenyPermission, permissions?.documents);
        this.medical = Permissions.fillPermission(basicDenyPermission, permissions?.medical);
        this.allergies = Permissions.fillPermission(nullPermission, permissions?.allergies);
        this.conditions = Permissions.fillPermission(nullPermission, permissions?.conditions);
        this.immunisations = Permissions.fillPermission(nullPermission, permissions?.immunisations);
        this.medications = Permissions.fillPermission(nullPermission, permissions?.medications);
        this.past_symptoms = Permissions.fillPermission(nullPermission, permissions?.past_symptoms);
        this.supplements = Permissions.fillPermission(nullPermission, permissions?.supplements);
        this.surgeries = Permissions.fillPermission(nullPermission, permissions?.surgeries);
        this.triggers = Permissions.fillPermission(nullPermission, permissions?.triggers);
        this.team = Permissions.fillPermission(teamDenyPermission, permissions?.team);
        this.photos = Permissions.fillPermission(basicDenyPermission, permissions?.photos);

        Object.seal(this);
    }

    static fillPermission<B extends AnyPermission, P extends Partial<AnyPermission> = Partial<B>>(
        base: B,
        from?: P | null,
    ): B {
        return Object.fromEntries(
            Object.entries(base).map(([ability, permission]) => [ability, from?.[ability as keyof P] ?? permission]),
        ) as B;
    }

    public test<S extends Section, A extends Ability<S>>(ability: A, section: S): ResolvedAbility<S, A> {
        return (
            this.hasAbility(section, ability) &&
            ((this[section][ability] === null && MedicalSections.includes(section as MedicalPermissionSection)
                ? this.test(ability as Ability<"medical">, "medical")
                : Boolean(this[section][ability])) as ResolvedAbility<S, A>)
        );
    }

    public compilePermissions<S extends Section, A extends Ability<S>>(
        ability: A | A[],
        section: S | S[],
    ): Permission<S, A>[] {
        const sections = (Array.isArray(section) ? section : [section]).filter((s) => Object.hasOwn(this, s));
        const abilities = Array.isArray(ability) ? ability : [ability];
        const permissions: Permission<S, A>[] = [];

        for (const s of sections) {
            permissions.push(
                ...abilities
                    .filter((a) => this.hasAbility(s, a))
                    .map((a): Permission<S, A> => ({ section: s, ability: a })),
            );
        }

        return permissions;
    }

    public hasSection<S extends Section>(section: S | string): section is S {
        return Object.hasOwn(this, section);
    }

    public hasAbility<S extends Section, A extends Ability<S>>(section: S | string, ability: A | string): ability is A {
        return this.hasSection(section) && Object.hasOwn(this[section], ability);
    }

    public can<S extends Section, A extends Ability<S>>(ability: A | A[], section: S | S[]): boolean {
        return this.compilePermissions(ability, section).every(({ section, ability }) => this.test(ability, section));
    }

    public canAny<S extends Section, A extends Ability<S>>(ability: A | A[], section: S | S[]): boolean {
        return this.compilePermissions(ability, section).some(({ section, ability }) => this.test(ability, section));
    }

    public cannot<S extends Section, A extends Ability<S>>(ability: A | A[], section: S | S[]): boolean {
        return !this.can(ability, section);
    }

    public cannotAny<S extends Section, A extends Ability<S>>(ability: A | A[], section: S | S[]): boolean {
        return !this.canAny(ability, section);
    }

    public onlyAllowed(): PartialProfilePermissions {
        return Object.fromEntries(
            (Object.entries(this) as PermissionEntry<Section>[])
                .map<PartialPermissionEntry<Section>>(([section, permission]) => [
                    section,
                    Object.fromEntries(
                        (Object.entries(permission) as PermissionSectionEntry<Section>[]).filter(
                            ([_ability, allowed]) => allowed,
                        ),
                    ) as PartialPermissionSection<Section>,
                ])
                .filter(([_section, abilities]) => Object.values(abilities).length > 0),
        );
    }

    public onlySet(): PartialProfilePermissions {
        return Object.fromEntries(
            (Object.entries(this) as PermissionEntry<Section>[])
                .map<PartialPermissionEntry<Section>>(([section, permission]) => [
                    section,
                    Object.fromEntries(
                        (Object.entries(permission) as PermissionSectionEntry<Section>[]).filter(
                            ([_ability, allowed]) => allowed !== null,
                        ),
                    ) as PartialPermissionSection<Section>,
                ])
                .filter(([_section, abilities]) => Object.values(abilities).length > 0),
        );
    }

    public matchesPermissions(other: PartialProfilePermissions) {
        const compare = new Permissions(other);

        return (Object.entries(this) as PermissionEntry<Section>[]).every(([section, permission]) => {
            return (Object.entries(permission) as PermissionSectionEntry<Section>[]).every(([ability, allowed]) => {
                return allowed === compare[section][ability];
            });
        });
    }
}
